import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Chip,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  Badge,
  Button
} from '@material-ui/core';
import {
  Search,
  FilterList,
  Clear,
  Star,
  StarBorder,
  Refresh
} from '@material-ui/icons';
import OptimizedExamSearchService from '../../services/OptimizedExamSearchService';
import './enhanced-exam-search.css';

const EnhancedExamSearch = ({ 
  onSelectExam, 
  selectedExam, 
  onReset,
  isLoading: externalLoading = false 
}) => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [examMetadata, setExamMetadata] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    examType: 'all', // 'all', 'dynamic', 'legacy'
    timerRange: [0, 300], // minutes
    sectionsRange: [1, 20],
    questionsRange: [1, 100],
    fuzzySearch: true,
    sortBy: 'relevance' // 'relevance', 'created', 'title', 'timer'
  });

  // Favorites state (stored in localStorage)
  const [favoriteExams, setFavoriteExams] = useState(() => {
    try {
      const saved = localStorage.getItem('favoriteExams');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // No automatic loading on component mount

  // Load exam metadata
  const loadExamMetadata = useCallback(async (forceRefresh = false) => {
    setIsLoadingMetadata(true);
    try {
      const metadata = await OptimizedExamSearchService.fetchExamMetadata(forceRefresh);
      setExamMetadata(metadata);
      
      // If we have a search query, re-run search with new metadata
      if (searchQuery.trim()) {
        performSearch(searchQuery, metadata);
      }
    } catch (error) {
      console.error('Error loading exam metadata:', error);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, [searchQuery]);

  // Debounced search function
  const debouncedSearch = useMemo(() => 
    OptimizedExamSearchService.createDebouncedSearch(
      (query, metadata = examMetadata) => performSearch(query, metadata),
      OptimizedExamSearchService.SEARCH_CONFIG.SEARCH_DEBOUNCE_MS
    ), [examMetadata]
  );

  // Perform search with current filters
  const performSearch = useCallback((query, metadata = examMetadata) => {
    if (!query.trim()) {
      setFilteredExams([]);
      return;
    }

    // Validate search query
    const validation = OptimizedExamSearchService.validateSearchQuery(query);
    if (!validation.valid) {
      console.warn('Invalid search query:', validation.error);
      return;
    }

    // Apply advanced filters first
    const filteredMetadata = OptimizedExamSearchService.applyAdvancedFilters(metadata, {
      examType: filters.examType,
      minTimer: filters.timerRange[0],
      maxTimer: filters.timerRange[1],
      minSections: filters.sectionsRange[0],
      maxSections: filters.sectionsRange[1],
      minQuestions: filters.questionsRange[0],
      maxQuestions: filters.questionsRange[1]
    });

    // Perform search
    const results = OptimizedExamSearchService.searchExams(filteredMetadata, query, {
      fuzzySearch: filters.fuzzySearch,
      sortBy: filters.sortBy,
      maxResults: 20
    });

    // Format results for display
    const formattedResults = results.map(OptimizedExamSearchService.formatExamForDisplay);
    
    setFilteredExams(formattedResults);

    // Save to recent searches
    OptimizedExamSearchService.saveRecentSearch(query);

  }, [examMetadata, filters]);

  // Handle search input change
  const handleSearchChange = useCallback((event) => {
    const query = event.target.value;
    setSearchQuery(query);
    
    if (query.trim()) {
      // Load metadata only when user starts typing
      if (examMetadata.length === 0) {
        loadExamMetadata().then(() => {
          debouncedSearch(query);
        });
      } else {
        debouncedSearch(query);
      }
    } else {
      setFilteredExams([]);
    }
  }, [debouncedSearch, examMetadata.length, loadExamMetadata]);

  // Handle exam selection
  const handleSelectExam = useCallback(async (exam) => {
    try {
      setSearchQuery(`${exam.title} (${exam.code})`);
      setFilteredExams([]);
      
      // Show loading state while fetching full data
      onSelectExam({
        ...exam,
        isLoading: true
      });
      
      // Always get full exam details when exam is selected
      console.log(`🔍 Loading full exam data for: ${exam.code}`);
      const fullExamData = await OptimizedExamSearchService.getExamDetails(exam.code);
      console.log(`✅ Full exam data loaded for: ${exam.code}`);
      
      // Call parent callback with full data
      onSelectExam({
        ...exam,
        fullData: fullExamData,
        isLoading: false
      });
      
    } catch (error) {
      console.error('❌ Error loading exam details:', error);
      // Call with error state
      onSelectExam({
        ...exam,
        fullData: null,
        isLoading: false,
        error: error.message
      });
    }
  }, [onSelectExam]);



  // Toggle favorite
  const toggleFavorite = useCallback((examCode) => {
    const newFavorites = favoriteExams.includes(examCode)
      ? favoriteExams.filter(code => code !== examCode)
      : [...favoriteExams, examCode];
    
    setFavoriteExams(newFavorites);
    localStorage.setItem('favoriteExams', JSON.stringify(newFavorites));
  }, [favoriteExams]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
    
    // Re-run search if we have a query
    if (searchQuery.trim()) {
      // Small delay to allow state update
      setTimeout(() => performSearch(searchQuery), 50);
    }
  }, [searchQuery, performSearch]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters({
      examType: 'all',
      timerRange: [0, 300],
      sectionsRange: [1, 20],
      questionsRange: [1, 100],
      fuzzySearch: true,
      sortBy: 'relevance'
    });
  }, []);

  // Handle reset
  const handleReset = useCallback(() => {
    setSearchQuery('');
    setFilteredExams([]);
    if (onReset) onReset();
  }, [onReset]);

  // Get popular exams for empty state
  const popularExams = useMemo(() => {
    if (examMetadata.length === 0) return [];
    return OptimizedExamSearchService.getPopularExams(examMetadata, 5)
      .map(OptimizedExamSearchService.formatExamForDisplay);
  }, [examMetadata]);

  return (
    <Box className="enhanced-search-container">
      {/* Search Input */}
      <Box mb={showFilters ? 2 : 3}>
        <Typography variant="body2" gutterBottom style={{ fontSize: 13, marginBottom: 8, fontWeight: 500 }}>
          🔍 Search for an assessment to clone:
        </Typography>
        <Box position="relative" className="search-input-container">
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Type assessment title, code, or keywords..."
            value={searchQuery}
            onChange={handleSearchChange}

            InputProps={{
              startAdornment: (
                <Search style={{ marginRight: 8, color: '#666', fontSize: 18 }} />
              ),
              endAdornment: (
                <Box display="flex" alignItems="center">
                  {(isLoadingMetadata || externalLoading) && (
                    <Tooltip title="Loading assessments...">
                      <CircularProgress size={16} style={{ marginRight: 8 }} />
                    </Tooltip>
                  )}
                  <Tooltip title="Advanced Filters">
                    <IconButton 
                      size="small" 
                      onClick={() => setShowFilters(!showFilters)}
                      style={{ color: showFilters ? '#1976d2' : '#666' }}
                      className="filter-button"
                    >
                      <Badge 
                        variant="dot" 
                        color="primary" 
                        invisible={
                          filters.examType === 'all' && 
                          filters.fuzzySearch === true &&
                          filters.timerRange[0] === 0 && 
                          filters.timerRange[1] === 300 &&
                          filters.sectionsRange[0] === 1 && 
                          filters.sectionsRange[1] === 20 &&
                          filters.sortBy === 'relevance'
                        }
                        className="filter-badge"
                      >
                        <FilterList fontSize="small" />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  {searchQuery && (
                    <Tooltip title="Clear Search">
                      <IconButton size="small" onClick={handleReset}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Refresh Data">
                    <IconButton 
                      size="small" 
                      onClick={() => loadExamMetadata(true)}
                      disabled={isLoadingMetadata}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ),
              style: { fontSize: 14 }
            }}
            style={{ 
              backgroundColor: 'white',
              borderRadius: 8,
              '& .MuiOutlinedInput-root': {
                borderRadius: 8
              }
            }}
            className="search-input"
          />

          
        </Box>
      </Box>

      {/* Advanced Filters */}
      <Collapse in={showFilters}>
        <Paper elevation={1} className={`filter-panel ${showFilters ? 'open' : ''}`} style={{ 
          padding: 16, 
          marginBottom: 16, 
          marginTop: 8,
          position: 'relative',
          zIndex: 1 
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              Advanced Filters
            </Typography>
            <Button size="small" onClick={resetFilters} style={{ fontSize: 11 }}>
              Reset All
            </Button>
          </Box>
          
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            {/* Exam Type Filter */}
            <FormControl size="small" variant="outlined">
              <InputLabel>Exam Type</InputLabel>
              <Select
                value={filters.examType}
                onChange={(e) => handleFilterChange('examType', e.target.value)}
                label="Exam Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="dynamic">Dynamic Only</MenuItem>
                <MenuItem value="legacy">Legacy Only</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By Filter */}
            <FormControl size="small" variant="outlined">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                label="Sort By"
              >
                <MenuItem value="relevance">Relevance</MenuItem>
                <MenuItem value="created">Date Created</MenuItem>
                <MenuItem value="title">Title (A-Z)</MenuItem>
                <MenuItem value="timer">Duration</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box mt={2}>
            {/* Timer Range */}
            <Typography variant="caption" gutterBottom>
              Timer Range: {filters.timerRange[0]} - {filters.timerRange[1]} minutes
            </Typography>
            <Slider
              value={filters.timerRange}
              onChange={(e, value) => handleFilterChange('timerRange', value)}
              valueLabelDisplay="auto"
              min={0}
              max={300}
              step={15}
              style={{ color: '#1976d2' }}
            />
          </Box>

          <Box mt={2}>
            {/* Sections Range */}
            <Typography variant="caption" gutterBottom>
              Sections: {filters.sectionsRange[0]} - {filters.sectionsRange[1]}
            </Typography>
            <Slider
              value={filters.sectionsRange}
              onChange={(e, value) => handleFilterChange('sectionsRange', value)}
              valueLabelDisplay="auto"
              min={1}
              max={20}
              step={1}
              style={{ color: '#1976d2' }}
            />
          </Box>

          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={filters.fuzzySearch}
                  onChange={(e) => handleFilterChange('fuzzySearch', e.target.checked)}
                  size="small"
                  color="primary"
                />
              }
              label={<Typography variant="caption">Enable Fuzzy Search</Typography>}
            />
            
            <Typography variant="caption" style={{ color: '#666' }}>
              {examMetadata.length} total assessments
            </Typography>
          </Box>
        </Paper>
      </Collapse>

      {/* Search Results */}
      {filteredExams.length > 0 && (
        <Box mt={showFilters ? 2 : 1} mb={2}>
          <Paper 
            elevation={3} 
            className="search-result-container"
            style={{ 
              maxHeight: 280,
              overflow: 'auto',
              backgroundColor: 'white',
              borderRadius: 8,
              border: '1px solid #e3f2fd',
              position: 'relative',
              zIndex: 1
            }}
          >
          <List dense>
            {filteredExams.map((exam) => (
              <ListItem 
                key={exam.code} 
                button 
                onClick={() => handleSelectExam(exam)}
                divider
                className={`search-result-item ${selectedExam?.code === exam.code ? 'selected' : ''}`}
                style={{ padding: '12px 16px' }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" style={{ fontSize: 14, fontWeight: 500 }}>
                        {exam.displayTitle}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip 
                          label={exam.typeLabel} 
                          color={exam.dynamic ? 'primary' : 'default'}
                          size="small"
                          className={`exam-type-chip ${exam.dynamic ? 'dynamic' : 'legacy'}`}
                          style={{ fontSize: 10, height: 20 }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(exam.code);
                          }}
                          className={`favorite-button ${favoriteExams.includes(exam.code) ? 'favorited' : ''}`}
                        >
                          {favoriteExams.includes(exam.code) ? 
                            <Star style={{ fontSize: 16, color: '#ffc107' }} /> : 
                            <StarBorder style={{ fontSize: 16, color: '#999' }} />
                          }
                        </IconButton>
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" style={{ fontSize: 11, color: '#666' }}>
                        {exam.displaySubtitle}
                      </Typography>
                      {exam.searchMatches && exam.searchMatches.length > 0 && (
                        <Box mt={0.5}>
                          <Typography variant="caption" style={{ fontSize: 10, color: '#1976d2' }}>
                            Matches: {exam.searchMatches.join(', ')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
        </Box>
      )}

      {/* No Results Message */}
      {searchQuery.trim() && filteredExams.length === 0 && !isLoadingMetadata && (
        <Box mt={showFilters ? 2 : 1} mb={2}>
          <Paper elevation={1} className="no-results-message" style={{ padding: 16, textAlign: 'center' }}>
            <Typography variant="body2" style={{ color: '#856404' }}>
              No assessments found for "{searchQuery}"
            </Typography>
            <Typography variant="caption" style={{ color: '#856404' }}>
              Try adjusting your search terms or filters
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Popular Exams (shown only when metadata is loaded and no search/selection) */}
      {!searchQuery.trim() && !selectedExam && examMetadata.length > 0 && popularExams.length > 0 && (
        <Box mt={2} mb={2}>
          <Typography variant="body2" gutterBottom style={{ fontSize: 13, marginBottom: 12, color: '#666', fontWeight: 500 }}>
            📋 Recently Created Assessments:
          </Typography>
          <Paper elevation={1} className="popular-exams-container" style={{ 
            maxHeight: 200, 
            overflow: 'auto',
            borderRadius: 8,
            border: '1px solid #f0f0f0'
          }}>
            <List dense>
              {popularExams.map((exam) => (
                <ListItem 
                  key={exam.code} 
                  button 
                  onClick={() => handleSelectExam(exam)}
                  divider
                  className="popular-exam-item"
                  style={{ padding: '8px 12px' }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" style={{ fontSize: 13 }}>
                          {exam.displayTitle}
                        </Typography>
                        <Chip 
                          label={exam.typeLabel} 
                          color={exam.dynamic ? 'primary' : 'default'}
                          size="small"
                          style={{ fontSize: 10, height: 18 }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" style={{ fontSize: 11 }}>
                        {exam.displaySubtitle} • {exam.createdFormatted}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {/* Loading State */}
      {isLoadingMetadata && (
        <Box mt={2} mb={2}>
          <Paper elevation={1} style={{ 
            padding: 20, 
            textAlign: 'center', 
            backgroundColor: '#f0f7ff',
            borderRadius: 8,
            border: '1px solid #e3f2fd'
          }}>
            <CircularProgress size={24} style={{ marginBottom: 12 }} />
            <Typography variant="body2" style={{ color: '#1976d2', fontSize: 13 }}>
              Loading assessments...
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Initial State Message */}
      {!searchQuery.trim() && !selectedExam && examMetadata.length === 0 && !isLoadingMetadata && (
        <Box mt={2} mb={2}>
          <Paper elevation={1} style={{ 
            padding: 20, 
            textAlign: 'center', 
            backgroundColor: '#f8f9fa', 
            borderRadius: 8,
            border: '1px solid #f0f0f0'
          }}>
            <Typography variant="body2" style={{ color: '#666', fontSize: 14, marginBottom: 8 }}>
              📋 Ready to Clone an Assessment
            </Typography>
            <Typography variant="body2" style={{ color: '#999', fontSize: 12 }}>
              Start typing in the search box above to find existing assessments
            </Typography>
          </Paper>
        </Box>
      )}

            {/* Selected Exam Details */}
      {selectedExam && (
        <Box mt={3} mb={2}>
          <Paper elevation={2} className="selected-exam-details" style={{ 
            padding: 16,
            borderRadius: 8,
            border: '1px solid #c8e6c9'
          }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center">
                {selectedExam.isLoading && (
                  <CircularProgress size={16} style={{ marginRight: 8 }} />
                )}
                <Typography variant="body2" style={{ color: '#2e7d32', fontSize: 13, fontWeight: 600 }}>
                  {selectedExam.isLoading ? '⏳ Loading Assessment...' : '✓ Selected Assessment'}
                </Typography>
              </Box>
              <Button size="small" onClick={handleReset} style={{ fontSize: 11 }}>
                Change Selection
              </Button>
            </Box>
            
            {selectedExam.error ? (
              <Typography variant="caption" style={{ fontSize: 11, color: '#d32f2f' }}>
                ❌ Error loading full data: {selectedExam.error}
              </Typography>
            ) : (
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                <Typography variant="caption" style={{ fontSize: 11 }}>
                  <strong>Title:</strong> {selectedExam.title}
                </Typography>
                <Typography variant="caption" style={{ fontSize: 11 }}>
                  <strong>Code:</strong> {selectedExam.code}
                </Typography>
                <Typography variant="caption" style={{ fontSize: 11 }}>
                  <strong>Duration:</strong> {selectedExam.timer} minutes
                </Typography>
                <Typography variant="caption" style={{ fontSize: 11 }}>
                  <strong>Type:</strong> {selectedExam.dynamic ? 'Dynamic' : 'Legacy'}
                </Typography>
                <Typography variant="caption" style={{ fontSize: 11 }}>
                  <strong>Sections:</strong> {selectedExam.sectionsCount}
                </Typography>
                <Typography variant="caption" style={{ fontSize: 11 }}>
                  <strong>Questions:</strong> {selectedExam.questionsCount}
                </Typography>
                {selectedExam.fullData && (
                  <Typography variant="caption" style={{ fontSize: 11, color: '#2e7d32', gridColumn: 'span 2' }}>
                    ✅ <strong>Full data loaded successfully</strong>
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Cache Stats (for debugging) */}
      {process.env.NODE_ENV === 'development' && (
        <Box mt={2}>
          <Typography variant="caption" style={{ fontSize: 10, color: '#999' }}>
            Cache: {examMetadata.length} metadata entries • 
            Results: {filteredExams.length} shown • 
            Favorites: {favoriteExams.length} items
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EnhancedExamSearch; 